import Image from "next/image";
import { motion } from "framer-motion";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";

// Import all SVG icons
import twitterXIcon from "@/public/brand-icons/twitter-x.svg";
import facebookIcon from "@/public/brand-icons/facebook.svg";
import youtubeIcon from "@/public/brand-icons/youtube.svg";
import instagramIcon from "@/public/brand-icons/instagram.svg";
import discordIcon from "@/public/brand-icons/discord.svg";
import linkedinIcon from "@/public/brand-icons/linkedin.svg";
import globeIcon from "@/public/brand-icons/globe-americas.svg";
import emailIcon from "@/public/brand-icons/envelope-fill.svg";
import tiktokIcon from "@/public/brand-icons/tiktok.svg";
import messageHeartIcon from "@/public/brand-icons/chat-square-heart.svg";
import redditIcon from "@/public/brand-icons/reddit.svg";
import indieHackersIcon from "@/public/brand-icons/indiehackers.svg";
import microLaunchIcon from "@/public/brand-icons/microlaunch.svg";
import productHuntIcon from "@/public/brand-icons/product-hunt-logo-orange-240.png";

// Platform color mapping
export const getPlatformColor = (platform: string, isDarkMode = false): string => {
  const colorMap: Record<string, string> = {
    twitter: isDarkMode ? "#D9D9D9" : "#000000",
    // X (formerly Twitter) - light gray in dark mode
    youtube: "#FF0000",
    facebook: "#1877F2",
    instagram: "#E4405F",
    tiktok: isDarkMode ? "#FFFFFF" : "#000000",
    // White in dark mode
    discord: "#5865F2",
    twitch: "#9146FF",
    reddit: "#FF4500",
    linkedin: "#0A66C2",
    email: "#6E56CF",
    website: "#2E7D32",
    custom: "#6E56CF",
    "message-heart": "#E91E63",
    // Pink color for message heart
    share: "#6E56CF",
    indiehackers: isDarkMode ? "#FFFFFF" : "#000000",
    // Black in light mode, white in dark mode
    microlaunch: "#0094FF",
    // Using the start color of the gradient
    producthunt: "#DA552F" // Product Hunt orange
  };
  return colorMap[platform.toLowerCase()] || "#6E56CF";
};

// Get the appropriate icon based on the path or platform name
export const getIconByPath = (iconPath: string) => {
  if (iconPath?.includes("twitter") || iconPath === "twitter") return twitterXIcon;
  if (iconPath?.includes("facebook") || iconPath === "facebook") return facebookIcon;
  if (iconPath?.includes("youtube") || iconPath === "youtube") return youtubeIcon;
  if (iconPath?.includes("instagram") || iconPath === "instagram") return instagramIcon;
  if (iconPath?.includes("tiktok") || iconPath === "tiktok") return tiktokIcon;
  if (iconPath?.includes("discord") || iconPath === "discord") return discordIcon;
  if (iconPath?.includes("linkedin") || iconPath === "linkedin") return linkedinIcon;
  if (iconPath?.includes("email") || iconPath?.includes("envelope") || iconPath === "email") return emailIcon;
  if (iconPath?.includes("globe") || iconPath?.includes("website") || iconPath === "website") return globeIcon;
  if (iconPath?.includes("message-heart") || iconPath === "message-heart") return messageHeartIcon;
  if (iconPath === "share") return messageHeartIcon; // Use message heart for share too
  if (iconPath?.includes("reddit") || iconPath === "reddit") return redditIcon;
  if (iconPath?.includes("indiehackers") || iconPath === "indiehackers") return indieHackersIcon;
  if (iconPath?.includes("microlaunch") || iconPath === "microlaunch") return microLaunchIcon;
  if (iconPath?.includes("producthunt") || iconPath === "producthunt") return productHuntIcon;
  return globeIcon; // Default to globe icon
};

// Get platform from icon path
export const getPlatformFromPath = (iconPath: string): string => {
  if (iconPath.includes("twitter-x")) return "twitter";
  if (iconPath.includes("facebook")) return "facebook";
  if (iconPath.includes("youtube")) return "youtube";
  if (iconPath.includes("instagram")) return "instagram";
  if (iconPath.includes("tiktok")) return "tiktok";
  if (iconPath.includes("discord")) return "discord";
  if (iconPath.includes("linkedin")) return "linkedin";
  if (iconPath.includes("envelope")) return "email";
  if (iconPath.includes("globe")) return "website";
  if (iconPath.includes("chat-square-heart")) return "message-heart";
  if (iconPath.includes("reddit")) return "reddit";
  if (iconPath.includes("indiehackers")) return "indiehackers";
  if (iconPath.includes("microlaunch")) return "microlaunch";
  if (iconPath.includes("product-hunt")) return "producthunt";
  return "custom";
};

// Get platform from title
export const getPlatformFromTitle = (title: string): string => {
  const titleLower = title.toLowerCase();
  if (titleLower.includes("youtube")) return "youtube";
  if (titleLower.includes("facebook")) return "facebook";
  if (titleLower.includes("instagram")) return "instagram";
  if (titleLower.includes("twitter") || titleLower.includes("x.com") || titleLower.includes("x account") || titleLower.includes("x profile")) return "twitter";
  if (titleLower.includes("tiktok")) return "tiktok";
  if (titleLower.includes("discord")) return "discord";
  if (titleLower.includes("linkedin")) return "linkedin";
  if (titleLower.includes("email") || titleLower.includes("subscribe")) return "email";
  if (titleLower.includes("share") || titleLower.includes("refer")) return "message-heart";
  if (titleLower.includes("reddit")) return "reddit";
  if (titleLower.includes("indie") || titleLower.includes("indiehackers")) return "indiehackers";
  if (titleLower.includes("micro") || titleLower.includes("microlaunch")) return "microlaunch";
  if (titleLower.includes("product hunt")) return "producthunt";
  return "website"; // Default
};

// Function to convert hex color to CSS filter with more subtle colors
export const getColorFilter = (hexColor: string, platform: string, isDarkMode = false): string => {
  // For common brand colors, return more subtle filters
  if (platform === "twitter") {
    return isDarkMode ? "invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(80%) contrast(90%)" // Light gray for dark mode
    : "invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(60%) contrast(90%)"; // Black for light mode
  }
  if (platform === "indiehackers") {
    return isDarkMode ? "invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)" // White for dark mode
    : "invert(0%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)"; // Black for light mode
  }
  if (platform === "microlaunch" || platform === "producthunt") {
    // No filter needed as it's an SVG with its own gradient or PNG with its own color
    return "none";
  }
  if (hexColor === "#FF0000") return "invert(15%) sepia(90%) saturate(5500%) hue-rotate(0deg) brightness(95%) contrast(95%)"; // YouTube
  if (hexColor === "#1877F2") return "invert(31%) sepia(85%) saturate(1800%) hue-rotate(206deg) brightness(95%) contrast(90%)"; // Facebook
  if (hexColor === "#E4405F") return "invert(40%) sepia(70%) saturate(1800%) hue-rotate(318deg) brightness(95%) contrast(85%)"; // Instagram
  if (hexColor === "#5865F2") return "invert(36%) sepia(40%) saturate(3500%) hue-rotate(230deg) brightness(95%) contrast(85%)"; // Discord
  if (hexColor === "#0A66C2") return "invert(29%) sepia(80%) saturate(1800%) hue-rotate(197deg) brightness(95%) contrast(85%)"; // LinkedIn

  if (platform === "tiktok") {
    return isDarkMode ? "invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)" // White for dark mode
    : "invert(0%) sepia(0%) saturate(0%) hue-rotate(324deg) brightness(60%) contrast(90%)"; // Black for light mode
  }
  if (hexColor === "#2E7D32") return "invert(32%) sepia(60%) saturate(700%) hue-rotate(93deg) brightness(90%) contrast(85%)"; // Website
  if (hexColor === "#6E56CF") return "invert(43%) sepia(50%) saturate(1800%) hue-rotate(235deg) brightness(90%) contrast(85%)"; // Email/Custom
  if (hexColor === "#E91E63") return "invert(37%) sepia(74%) saturate(2404%) hue-rotate(316deg) brightness(95%) contrast(90%)"; // Message Heart
  if (hexColor === "#FF4500") return "invert(40%) sepia(75%) saturate(6402%) hue-rotate(360deg) brightness(105%) contrast(107%)"; // Reddit - proper orange color
  // Default filter for other colors
  return "invert(43%) sepia(50%) saturate(1800%) hue-rotate(235deg) brightness(90%) contrast(85%)"; // Default purple
};

// Reusable PlatformIcon component
interface PlatformIconProps {
  platform: string;
  iconPath?: string;
  size?: "sm" | "md" | "lg";
  customSize?: {
    container?: string; // Custom Tailwind classes for container size
    icon?: string; // Custom Tailwind classes for icon size
    dimensions?: number; // Custom dimensions for the image
  };
  animated?: boolean;
  plainContainer?: boolean;
}
export const PlatformIcon = ({
  platform,
  iconPath,
  size = "md",
  customSize,
  animated = true,
  plainContainer = false
}: PlatformIconProps) => {
  const {
    resolvedTheme
  } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const isDarkMode = mounted ? resolvedTheme === "dark" : true;
  const actualPlatform = platform || (iconPath ? getPlatformFromPath(iconPath) : "custom");
  const color = getPlatformColor(actualPlatform, isDarkMode);
  const icon = getIconByPath(iconPath || actualPlatform);
  const filterValue = `brightness(0) saturate(100%) ${getColorFilter(color, actualPlatform, isDarkMode)}`;

  // Size mappings
  const sizeMap = {
    sm: {
      container: "h-5 w-5",
      icon: "h-3.5 w-3.5",
      dimensions: 14
    },
    md: {
      container: "h-6 w-6",
      icon: "h-4 w-4",
      dimensions: 16
    },
    lg: {
      container: "h-7 w-7",
      icon: "h-5 w-5",
      dimensions: 20
    }
  };

  // Use custom sizes if provided, otherwise fall back to preset sizes
  const {
    container,
    icon: iconSize,
    dimensions
  } = customSize ? {
    container: customSize.container || sizeMap[size].container,
    icon: customSize.icon || sizeMap[size].icon,
    dimensions: customSize.dimensions || sizeMap[size].dimensions
  } : sizeMap[size];
  const iconElement = <div className={`flex ${container} items-center justify-center rounded-full`} style={{
    backgroundColor: plainContainer ? "transparent" : `${color}08`,
    boxShadow: plainContainer ? "none" : `0 1px 2px rgba(0, 0, 0, 0.05)`
  }}>
      <Image src={icon} alt={`${actualPlatform} icon`} width={dimensions} height={dimensions} className={`${iconSize} object-contain`} style={{
      opacity: 0.8,
      filter: filterValue
    }} />
    </div>;
  if (animated) {
    return <motion.div whileHover={{
      scale: 1.05
    }} transition={{
      duration: 0.2
    }}>
        {iconElement}
      </motion.div>;
  }
  return iconElement;
};